import React, { useState } from 'react';
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel,
  MenuItem, Stack, Switch, TextField,
} from '@mui/material';
import { useSnackbar } from 'notistack';

import { peopleService, type Guardian, type GuardianRelation } from '../../services/peopleService';
import { cnicDigits, formatCnic } from '../../utils/cnic';
import { isValidPhone } from '../admissions/form/admissionFormFields';

interface Props {
  open: boolean;
  familyId: string;
  /** Edit this guardian; add a new one when absent. */
  guardian?: Guardian | null;
  onClose: () => void;
  onSaved: () => void;
  /** The CNIC already belongs to a guardian of another family. */
  onOpenFamily?: (familyId: string) => void;
}

const RELATIONS: Array<{ value: GuardianRelation; label: string }> = [
  { value: 'father', label: 'Father' },
  { value: 'mother', label: 'Mother' },
  { value: 'guardian', label: 'Guardian' },
];

/** Add or edit one guardian of a family. */
const GuardianDialog: React.FC<Props> = ({ open, familyId, guardian, onClose, onSaved, onOpenFamily }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [form, setForm] = useState({
    relation: guardian?.relation ?? 'father' as GuardianRelation,
    name: guardian?.name ?? '',
    cnic: guardian?.cnicIsPlaceholder ? '' : guardian?.cnic ?? '',
    phone: guardian?.phone ?? '',
    email: guardian?.email ?? '',
    occupation: guardian?.occupation ?? '',
    isEmergencyContact: guardian?.isEmergencyContact ?? true,
    canPickup: guardian?.canPickup ?? true,
  });
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [elsewhere, setElsewhere] = useState<{ message: string; familyId: string } | null>(null);
  const set = (k: keyof typeof form) => (v: any) => setForm(f => ({ ...f, [k]: v }));

  const errors = {
    name: !form.name.trim() ? 'Enter the guardian\'s name.' : '',
    cnic: cnicDigits(form.cnic).length !== 13 ? 'CNIC must be 13 digits.' : '',
    phone: !isValidPhone(form.phone) ? 'Enter a valid phone number.' : '',
    email: form.email && !/^\S+@\S+\.\S+$/.test(form.email) ? 'Enter a valid email address.' : '',
  };
  const invalid = Object.values(errors).some(Boolean);

  const save = async () => {
    setTouched(true);
    if (invalid) return;
    setSaving(true);
    setElsewhere(null);
    const payload = {
      relation: form.relation,
      name: form.name.trim(),
      cnic: cnicDigits(form.cnic),
      phone: form.phone.trim(),
      email: form.email.trim() || undefined,
      occupation: form.occupation.trim() || undefined,
      isEmergencyContact: form.isEmergencyContact,
      canPickup: form.canPickup,
    };
    try {
      if (guardian) await peopleService.updateGuardian(guardian._id, payload);
      else await peopleService.addGuardianToFamily(familyId, payload);
      enqueueSnackbar(guardian ? 'Guardian updated' : 'Guardian added', { variant: 'success' });
      onSaved();
    } catch (err: any) {
      if (err?.data?.existingFamilyId) {
        setElsewhere({ message: err.message, familyId: err.data.existingFamilyId });
      } else {
        enqueueSnackbar(err?.message || 'Could not save the guardian', { variant: 'error' });
      }
    } finally {
      setSaving(false);
    }
  };

  const show = (k: keyof typeof errors) => (touched ? errors[k] : '');

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{guardian ? `Edit ${guardian.name}` : 'Add a guardian'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {elsewhere && (
            <Alert
              severity="warning"
              action={onOpenFamily && (
                <Button color="inherit" size="small" onClick={() => onOpenFamily(elsewhere.familyId)} sx={{ textTransform: 'none' }}>
                  Open that family
                </Button>
              )}
            >
              {elsewhere.message} If it is the same household, merge the two families instead.
            </Alert>
          )}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField select label="Relation" value={form.relation} onChange={e => set('relation')(e.target.value)} sx={{ minWidth: 150 }}>
              {RELATIONS.map(r => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
            </TextField>
            <TextField label="Full name" required fullWidth value={form.name} onChange={e => set('name')(e.target.value)}
              error={!!show('name')} helperText={show('name') || ' '} />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField label="CNIC" required fullWidth placeholder="35202-1234567-1"
              value={formatCnic(form.cnic)} onChange={e => set('cnic')(cnicDigits(e.target.value))}
              error={!!show('cnic')} helperText={show('cnic') || ' '} inputProps={{ inputMode: 'numeric' }} />
            <TextField label="Phone" required fullWidth placeholder="03001234567"
              value={form.phone} onChange={e => set('phone')(e.target.value)}
              error={!!show('phone')} helperText={show('phone') || ' '} inputProps={{ inputMode: 'tel' }} />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField label="Email" fullWidth value={form.email} onChange={e => set('email')(e.target.value)}
              error={!!show('email')} helperText={show('email') || ' '} />
            <TextField label="Occupation" fullWidth value={form.occupation} onChange={e => set('occupation')(e.target.value)} helperText=" " />
          </Stack>
          <Stack direction="row" spacing={3}>
            <FormControlLabel control={<Switch checked={form.isEmergencyContact} onChange={e => set('isEmergencyContact')(e.target.checked)} />} label="Emergency contact" />
            <FormControlLabel control={<Switch checked={form.canPickup} onChange={e => set('canPickup')(e.target.checked)} />} label="Can pick up the children" />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button variant="contained" disableElevation onClick={save} disabled={saving || (touched && invalid)} sx={{ textTransform: 'none' }}>
          {saving ? 'Saving…' : guardian ? 'Save changes' : 'Add guardian'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GuardianDialog;
